import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { refreshAccessToken } from "../api/client";
import { linkAnalyticsSession } from "../analytics/analytics";
import { userInfoRequest, authFailure } from "../store/slice/authSlice";
import type { RootState } from "../store/store";

export default function LoginSuccessPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const startedRef = useRef(false);
  const redirectedRef = useRef(false);

  const authUser = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const userInfoFetched = useSelector((state: RootState) => state.auth.userInfoFetched);
  const loginError = useSelector((state: RootState) => state.auth.loginError);
  const loading = useSelector((state: RootState) => state.auth.loading);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    localStorage.setItem("socialLoginInProgress", "true");

    void (async () => {
      try {
        await refreshAccessToken({ allowDuringSocialLogin: true });

        try {
          void linkAnalyticsSession().catch(() => {
          });
        } catch {}

        localStorage.removeItem("socialLoginInProgress");
        window.history.replaceState({}, document.title, "/login/success");
        dispatch(userInfoRequest());
      } catch (error) {
        console.error("LoginSuccessPage 처리 중 오류:", error);
        localStorage.removeItem("socialLoginInProgress");
        dispatch(authFailure("소셜 로그인 세션을 복구하지 못했습니다."));
        navigate("/m/home", { replace: true });
      }
    })();
  }, [navigate, dispatch]);

  useEffect(() => {
    if (redirectedRef.current) return;

    const resolvedUserId = authUser?.id;
    const hasValidUser = typeof resolvedUserId === "number" && resolvedUserId > 0;

    if (hasValidUser && isAuthenticated) {
      redirectedRef.current = true;
      localStorage.removeItem("socialLoginInProgress");
      navigate("/m/home", { replace: true });
      return;
    }

    if (userInfoFetched && !loading && !hasValidUser) {
      redirectedRef.current = true;
      localStorage.removeItem("socialLoginInProgress");
      dispatch(authFailure(loginError || "소셜 로그인 후 사용자 정보를 불러오지 못했습니다."));
      navigate("/m/home", { replace: true });
    }
  }, [
    authUser?.id,
    isAuthenticated,
    userInfoFetched,
    loading,
    loginError,
    dispatch,
    navigate,
  ]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          textAlign: "center",
          border: "1px solid #e5e7eb",
          borderRadius: "16px",
          padding: "32px 24px",
          boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "#111827" }}>
          로그인 처리 중입니다
        </h2>
        <p
          style={{
            marginTop: "12px",
            marginBottom: 0,
            fontSize: "15px",
            lineHeight: 1.6,
            color: "#6b7280",
          }}
        >
          소셜 로그인 정보를 확인하고 사용자 정보를 불러오고 있습니다.
        </p>
      </div>
    </div>
  );
}
